import { useState, useEffect, useMemo } from 'react';
import { trpc } from '../utils/trpc';
import '../styles/EditEventModal.css';
import TagManagerModal from './TagManagerModal';

//Props expected by EditEventModal component
interface EditEventProps {
  isOpen: boolean; //whether the modal is open or not
  onClose: () => void; //closes the modal
  event: any; // Event object containing details of the event to be edited
  groupId: string; // ID of the group to which the event belongs
}

//Shape of a tag object, used for managing event tags in the TagManagerModal
interface Tag {
  id: number;
  name: string;
  category_id?: number;
}

//Main component for editing an event, including its details and associated tags
function EditEventModal({ isOpen, onClose, event, groupId }: EditEventProps) {
  console.log('Date in EditEventModal:', event?.date);
  //Access to TRPC utilities for cache management and refetching queries after mutations
  const utils = trpc.useUtils();

  //State variables for managing form inputs and tag modal visibility
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [capacity, setCapacity] = useState<number | ''>('');
  const [duration_minutes, setDuration] = useState<number | ''>('');
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  //Fetch tags for the event
  const { data: eventTags } = trpc.tags.getEventTags.useQuery(
    { eventId: event?.event_id || '' },
    { enabled: !!event?.event_id },
  );

  /** Memoize tags */
  const currentTags: Tag[] = useMemo(() => {
    if (!eventTags || !Array.isArray(eventTags)) return [];
    //Map API response to Tag object format
    return eventTags.map((entry) => ({
      id: entry.tag_id,
      name:
        (Array.isArray((entry as any).tags)
          ? (entry as any).tags[0]?.name
          : (entry as any).tags?.name) ?? 'Unknown',
    }));
  }, [eventTags]);

  //populate form fields with existing event data when the modal opens or when the event prop changes
  useEffect(() => {
    if (!event) return;
    //set name and description, using empty strings as fallbacks if values are null or undefined
    setName(event.event_name ?? '');
    setDescription(event.description ?? '');

    //format date to match the expected input format for datetime-local
    if (event.date) {
      const d = new Date(event.date);
      setDate(d.toISOString().slice(0, 16));
    } else {
      setDate('');
    }
    //set capacity, ensures it is a number or an empty string
    setCapacity(event.max_capacity != null ? Number(event.max_capacity) : '');
    //set duration, ensures it is a number or an empty string
    setDuration(event.duration_minutes != null ? Number(event.duration_minutes) : '');
  }, [event]);

  //mutation for updating the event
  const updateEvent = trpc.event.updateEvent.useMutation({
    onSuccess: async () => {
      try {
        if (event?.event_id) {
          // Refetch updated event details
          await utils.event.getEventById.invalidate({
            eventId: event.event_id,
          });
        }
        //refetch list of events for the group to reflect any changes in the event list
        await utils.event.getEventsByGroupId.invalidate({ groupId });
      } catch (err) {
        console.error('Refetch failed:', err);
      }
      //closes modal after successful update
      onClose();
    },
    onError: (err) => {
      //log update failure
      console.error('Update failed:', err);
    },
  });

  //handles the save button click, validates input and triggers the update mutation with the new event details
  const handleSave = async () => {
    if (!event?.event_id) {
      console.error('Missing event ID');
      return;
    }

    if (!name.trim()) return;

    try {
      await updateEvent.mutateAsync({
        eventId: event.event_id,
        eventName: name,
        description: description || undefined,
        date: event.date ? new Date(event.date).toISOString() : undefined,
        maxCapacity: capacity === '' || Number.isNaN(capacity) ? undefined : capacity,
        duration_minutes:
          duration_minutes === '' || Number.isNaN(duration_minutes)
            ? undefined
            : duration_minutes,
      });
    } catch (err) {
      console.error('Failed to update event:', err);
    }
  };

  if (!isOpen || !event) return null;

  return (
    <div className="edit-event-modal-overlay">
      <div className="edit-event-modal-content">
        <div className="edit-event-modal-header">
          <h2>Edit Event</h2>
          {/* Close button to exit the modal */}
          <button className="edit-event-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="edit-event-modal-body">
          {/* Form fields for editing event details */}
          <label data-testid="event-name-label">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />

          <label data-testid="event-description-label">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <label data-testid="event-date-label">Date</label>
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <label data-testid="event-duration-label">Event Duration</label>
          <input
            type="number"
            value={duration_minutes === '' ? '' : duration_minutes}
            onChange={(e) => {
              const val = e.target.value;
              setDuration(val === '' ? '' : Number(val));
            }}
          />

          <label data-testid="event-capacity-label">Capacity</label>
          <input
            type="number"
            value={capacity === '' ? '' : capacity}
            onChange={(e) => {
              const val = e.target.value;
              setCapacity(val === '' ? '' : Number(val));
            }}
          />
          {/* Button to open the tag editing modal, allows users to manage tags associated with the event */}
          <button className="edit-event-tags-btn" onClick={() => setIsTagModalOpen(true)}>
            Edit Interests
          </button>
        </div>

        <div className="edit-event-modal-actions">
          <button
            className="edit-event-cancel-btn"
            onClick={onClose}
            disabled={updateEvent.isPending}
          >
            Cancel
          </button>

          <button
            className="edit-event-save-btn"
            onClick={handleSave}
            disabled={updateEvent.isPending || !name.trim()}
          >
            {updateEvent.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      {/* TagManagerModal is a child component that allows users to edit the tags
       associated with the event. It is conditionally rendered based on the
        isTagModalOpen state. It receives the current tags, event ID, and a 
        callback to close the modal as props. */}
      <TagManagerModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        mode="edit"
        initialTags={currentTags}
        originalTags={currentTags}
        min={1}
        max={10}
        profileType="event"
        eventId={event.event_id}
      />
    </div>
  );
}

export default EditEventModal;
